import {Component, Input, Output, EventEmitter, NgZone, OnDestroy, AfterViewInit} from '@angular/core';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import {SearchBoxMapComponent} from "./search-box-map/search-box-map.component";

@Component({
  selector: 'app-point-map',
  standalone: true,
  imports: [
    GoogleMap,
    MapMarker,
    SearchBoxMapComponent
  ],
  templateUrl: './point-map.component.html',
  styleUrl: './point-map.component.scss',
})
export class PointMapComponent implements AfterViewInit, OnDestroy {
  private destroyed = false;
  private iframeObserver?: MutationObserver;

  @Input() center: google.maps.LatLngLiteral = {
    lat: 48.2082,
    lng: 16.3738
  };
  @Input() zoom = 10;
  @Output() addressSelected = new EventEmitter<{
  lat: number;
  lng: number;
  postcode?: string;
  place?: string;
  street?: string;
  houseNumber?: string;
  }>();
  @Output() searchedPlace = new EventEmitter<{}>();

  selectedPosition?: google.maps.LatLngLiteral;
  searchBoxMarker: {
    position: google.maps.LatLngLiteral,
    title: string | undefined,
    address: string | undefined}[] = [];

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    const root = document.body;
    const sanitize = (el: ParentNode) =>
      el.querySelectorAll('iframe').forEach((iframe) => {
        if (!iframe.getAttribute('src')) {
          iframe.setAttribute('src', 'about:blank');
        }
      });

    this.ngZone.runOutsideAngular(() => {
      sanitize(root);
      this.iframeObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.target instanceof HTMLIFrameElement) {
            if (!mutation.target.getAttribute('src')) mutation.target.setAttribute('src', 'about:blank');
            continue;
          }
           if (mutation instanceof HTMLElement) {
                sanitize(mutation);
              }
        }
      });
      this.iframeObserver.observe(root, {
        childList: true,
        attributeFilter: ['src'],
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.iframeObserver?.disconnect();
  }

  onMapClick(event: google.maps.MapMouseEvent) {
  if (this.destroyed || !event.latLng) return;

  const coords = { lat: event.latLng.lat(), lng: event.latLng.lng() };

  this.selectedPosition = coords;
  this.searchBoxMarker = [];

  this.addressSelected.emit({ lat: coords.lat, lng: coords.lng,});

  const geocoder = new google.maps.Geocoder();

  geocoder.geocode({ location: coords }, (results, status) => {
    if (this.destroyed || status !== 'OK' || !results?.length) return;
    const components = results[0].address_components;
    const get = (type: string) =>
      components.find(c => c.types.includes(type))?.long_name ?? '';

    this.ngZone.run(() => {
        if (this.destroyed) return;
        this.addressSelected.emit({
          lat: coords.lat,
          lng: coords.lng,
          postcode: get('postal_code'),
          place:
            get('locality') ||
            get('postal_town'),
          street: get('route'),
          houseNumber: get('street_number'),
        });
      });
    });
  }

  onPlaceLogged(place: google.maps.places.Place[]){
    if (this.destroyed) return;
    this.ngZone.run(() => {
      if (this.destroyed) return;
      this.selectedPosition = undefined;

      this.searchBoxMarker = place
        .filter (p => p.location)
        .map (p => ({
          position:{lat: p.location!.lat(), lng: p.location!.lng()},
          title: p.displayName ?? undefined,
          address: p.formattedAddress ?? undefined,
        }));
      const first = place[0];
      if (first?.location) {
        const comps = first.addressComponents ?? [];
        const get = (type: string) =>
          comps.find(c => c.types.includes(type))?.longText ?? '';

        this.addressSelected.emit({
          lat: first.location.lat(),
          lng: first.location.lng(),
          postcode: get('postal_code'),
          place:
            get('locality') ||
            get('postal_town'),
          street: get('route'),
          houseNumber: get('street_number'),
        });
      }

      this.searchedPlace.emit();
    });

    if (this.searchBoxMarker?.length) {
      this.center = this.searchBoxMarker[0].position;
      this.zoom = 15;
    }
  }
}
